import { apiClient } from './apiClient';
import { getMyLibrary } from './libraryService';
import { LicenseType as LibLicenseType, LicenseStatus as LibLicenseStatus } from '../types/library';

// Backend LicenseType enum values
export enum LicenseType {
  Purchase = 1,
  Rental = 2,
  Subscription = 3,
}

export interface BulkAssignLicensesDto {
  bookIds: string[]; // Multiple book IDs
  licenseType: LicenseType;
  expirationDate?: string; // ISO date string, null for perpetual
  maxConcurrentDevices: number;
  studentEmails: string[];
}

export interface BulkAssignLicensesResultDto {
  totalRequested: number;
  successfulAssignments: number;
  failedAssignments: number;
  errors: {
    email: string;
    bookId?: string;
    bookTitle: string;
    error: string;
  }[];
  assignedLicenses: {
    id: string;
    studentEmail: string;
    bookId: string;
    bookTitle: string;
    expirationDate?: string;
  }[];
}

export interface LicenseDto {
  id: string;
  bookId: string;
  studentId: string;
  studentEmail?: string;
  licenseType: string;
  status: string;
  activationDate: string;
  expirationDate?: string;
  maxConcurrentDevices: number;
}

/**
 * Bulk assign licenses to multiple students
 */
export async function bulkAssignLicenses(
  dto: BulkAssignLicensesDto
): Promise<BulkAssignLicensesResultDto> {
  const response = await apiClient.post<BulkAssignLicensesResultDto>(
    '/api/app/licenses-extn/bulk-assign',
    dto
  );
  return response.data;
}

/**
 * Get all licenses (with filtering)
 */
export async function getLicenses(params?: {
  bookId?: string;
  studentId?: string;
  status?: string;
  skipCount?: number;
  maxResultCount?: number;
}): Promise<{ items: LicenseDto[]; totalCount: number }> {
  const response = await apiClient.get('/api/app/licenses', { params });
  return response.data;
}

/**
 * Get license by ID
 */
export async function getLicenseById(id: string): Promise<LicenseDto> {
  const response = await apiClient.get(`/api/app/licenses/${id}`);
  return response.data;
}

/**
 * Revoke a license
 */
export async function revokeLicense(id: string, reason: string): Promise<void> {
  await apiClient.post(`/api/app/licenses-extn/${id}/revoke`, { reason });
}

/**
 * Suspend a license
 */
export async function suspendLicense(id: string, reason: string): Promise<void> {
  await apiClient.post(`/api/app/licenses-extn/${id}/suspend`, { reason });
}

/**
 * Reactivate a suspended license
 */
export async function reactivateLicense(id: string): Promise<void> {
  await apiClient.post(`/api/app/licenses-extn/${id}/reactivate`);
}

export interface UserBookLicense {
  licenseType: LibLicenseType;
  status: LibLicenseStatus;
  expirationDate?: Date | null;
}

/**
 * Get the current user's license for a specific book.
 * Fetches from the library endpoint which includes full license details.
 */
export async function getUserLicenseForBook(bookId: string): Promise<UserBookLicense | null> {
  const library = await getMyLibrary();
  const entry = library.find(item => item.license.bookId === bookId);

  if (!entry) return null;

  return {
    licenseType: entry.license.licenseType,
    status: entry.license.status,
    expirationDate: entry.license.expirationDate
      ? new Date(entry.license.expirationDate)
      : null,
  };
}
